import { ThreeDotVerticalIcon } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import clsx from 'clsx'
import React, { useMemo, useState } from 'react'

import { BottomSheet, Dropdown } from '@/components'
import { useToggle } from '@/hooks'
import { useGuardedNavigate } from '@/hooks/useGuardedNavigate'
import { HomeQualityCheckStatus, HomeQualityCheckSummary } from '@/types/HomeQualityCheck'
import { formatDate } from '@/util'

import { HomeReadiness } from '../HomeReadiness'
import { HomeQualityCheckSummaryMedia } from './Media'

type Props = {
  homeQualityCheck: HomeQualityCheckSummary
  refetchOnSuccess?: () => void
  setHomeQualityCheckId?: (homeQualityCheckId?: number) => void
}

type SummaryOption = {
  className: string
  label: string
}

export const MobileHomeQualityCheckSummaryCard = ({
  homeQualityCheck,
  refetchOnSuccess,
  setHomeQualityCheckId,
}: Props) => {
  const {
    id,
    home,
    notes,
    precedingReservationId,
    precedingReservationMoveOutDate,
    upcomingReservationId,
    upcomingReservationMoveInDate,
  } = homeQualityCheck
  const navigate = useGuardedNavigate()
  const { isShown: mediaModalShown, toggle: mediaModalToggle } = useToggle()
  const { isShown: readinessModalShown, toggle: readinessModalToggle } = useToggle()

  const options = useMemo(() => {
    let options: SummaryOption[] = []
    if (homeQualityCheck.status === HomeQualityCheckStatus.NOT_READY) {
      options.push({ label: 'Mark Home as Ready', className: 'text-action-primary' })
      options.push({ label: 'Continue', className: 'text-action-primary' })
    }
    options.push({ label: 'View Previous Reservation', className: 'text-action-input' })
    if (homeQualityCheck.upcomingReservationId) {
      options.push({ label: 'View Upcoming Reservation', className: 'text-action-tertiary' })
    }
    if (homeQualityCheck.attachments && homeQualityCheck.attachments.length > 0) {
      options.push({ label: 'View Media', className: 'text-action-input' })
    }
    return options
  }, [homeQualityCheck])

  const [isOpen, setIsOpen] = useState(false)

  const handleClearHomeQualityCheckId = () => {
    if (setHomeQualityCheckId) {
      setHomeQualityCheckId(undefined)
    }
    refetchOnSuccess?.()
  }

  const homeHomeQualityCheckAction = (type: string) => {
    if (id) {
      switch (type) {
        case 'Mark Home as Ready': {
          setIsOpen(false)
          readinessModalToggle()
          break
        }
        case 'Continue': {
          setIsOpen(false)
          if (setHomeQualityCheckId) {
            setHomeQualityCheckId(id)
          } else {
            navigate(`/homes/${home.id}`)
          }
          break
        }
        case 'View Previous Reservation': {
          setIsOpen(false)
          navigate(`/reservations/${precedingReservationId}`)
          break
        }
        case 'View Upcoming Reservation': {
          setIsOpen(false)
          navigate(`/reservations/${upcomingReservationId}`)
          break
        }
        case 'View Media': {
          setIsOpen(false)
          mediaModalToggle()
          break
        }
        default: {
          return
        }
      }
    }
  }

  return (
    <BoxStyled
      className={clsx(homeQualityCheck.status === HomeQualityCheckStatus.READY ? 'bg-green-100' : 'bg-red-100')}
      onClick={() => {
        if (id) {
          setHomeQualityCheckId ? setHomeQualityCheckId(id) : navigate(`/homes/${home.id}`)
        }
      }}
      role="button"
    >
      {home.searchDocument ? (
        <div className="mb-2 flex w-full items-center justify-between">
          <p className="text-p-sm">{home.searchDocument}</p>
        </div>
      ) : null}

      <div className="home-quality-check-summary-information grid grid-cols-3">
        <div className="bordered-column">
          <h6>Previous</h6>
          <p>
            {`Move-out: ${
              precedingReservationMoveOutDate
                ? formatDate(new Date(precedingReservationMoveOutDate || ''), 'MM-dd-yyyy')
                : 'Pending'
            }`}
          </p>
        </div>
        <div className="bordered-column">
          <h6>Upcoming</h6>
          <p>{`Move-in: ${
            upcomingReservationMoveInDate
              ? formatDate(new Date(upcomingReservationMoveInDate || ''), 'MM-dd-yyyy')
              : 'N/A'
          }`}</p>
        </div>
        <div className="pl-2">
          <div className="mr-4">
            <h6>Notes</h6>
            <p>{notes}</p>
          </div>
          <div
            className="absolute right-4 ml-auto flex items-center justify-center pr-2"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <Dropdown
              className="my-2 md:my-0 relative"
              dropdownWrapperClassName="-top-18 right-8"
              expanded={isOpen}
              onHideDropdown={() => setIsOpen(false)}
              renderToggle={() => {
                return <ThreeDotVerticalIcon className="text-primary" height={16} />
              }}
              setExpanded={setIsOpen}
            >
              <div className="right-0 top-0 flex flex-col py-2">
                {options.map((o, i) => {
                  return (
                    <button
                      className={`mb-1 w-full cursor-pointer px-6 py-3 leading-none hover:bg-action-secondary ${o.className}`}
                      key={i}
                      onClick={() => homeHomeQualityCheckAction(o.label)}
                    >
                      {o.label}
                    </button>
                  )
                })}
              </div>
            </Dropdown>
          </div>
        </div>
      </div>
      {readinessModalShown && id ? (
        <BottomSheet header="Mark Home as Ready" isVisible={readinessModalShown} onClose={readinessModalToggle}>
          <HomeReadiness
            homeQualityCheckId={id}
            onClearHomeQualityCheckId={handleClearHomeQualityCheckId}
            toggle={readinessModalToggle}
          />
        </BottomSheet>
      ) : null}
      {mediaModalShown && homeQualityCheck.attachments && homeQualityCheck.attachments.length > 0 ? (
        <BottomSheet header="Media" isVisible={mediaModalShown} onClose={mediaModalToggle}>
          <HomeQualityCheckSummaryMedia attachments={homeQualityCheck.attachments} />
        </BottomSheet>
      ) : null}
    </BoxStyled>
  )
}

const BoxStyled = styled.div`
  position: relative;
  padding: 0.75rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 1rem;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.12);
  cursor: pointer;

  .home-quality-check-summary-information {
    div {
      display: flex;
      flex-direction: column;
    }

    h6 {
      color: #787573;
      font-size: var(--text-p-xs);
    }
    p {
      font-size: var(--text-p-xs);
      line-height: 1.2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    p:hover {
      overflow: visible;
      overflow-wrap: break-word;
      white-space: normal;
      height: auto;
    }
  }

  .bordered-column {
    border-right-width: 1px;
    padding-left: 0.5rem;
    border-color: #e7e6e4;
  }
`
